import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Stack, Select, MenuItem, FormControl, Button, Divider, IconButton, Tooltip,
  TextField, Chip, Menu, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WorkIcon from '@mui/icons-material/Work';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InboxIcon from '@mui/icons-material/Inbox';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import type { InvoiceLineDetail, POLineType, TaxMode } from '../data/types';
import { INVOICE_STATUSES } from '../data/types';
import { BRAND, STATUS_TONE, getStatusTone } from '../theme/tokens';
import {
  BTN_STATUS_GREEN, BTN_STANDARD, BTN_GHOST_DOC, BTN_GHOST_DANGER, BTN_PURPLE, LINK_BLUE,
  PO_STICKY_LIFECYCLE_SX, PO_STICKY_LIFECYCLE_PUSH, PO_STICKY_LIFECYCLE_TRAIL,
} from '../theme/poButtonTokens';
import { StatusChip } from '../components/StatusChip';
import { InvoiceEditForm } from '../components/InvoiceEditForm';
import { InfoableButton, type ButtonInfo } from '../components/ImportResolutionBadge';
import { AttachFileZone } from '../components/AttachFileZone';
import { AiImportBanner } from '../components/AiImportBanner';
import { SupplierImportPreviewDialog } from '../components/SupplierImportPreviewDialog';
import {
  buildImportLineResolution, buildInitialImportResolutions, applyImportPreviewAction,
  batchImportPreviewAction, countPreviewNewLines,
  resolveImportPreviewInternal, type ImportPreviewDeps, type ParsedImportLine,
} from '../utils/importPreviewWorkflow';
import type { ImportLineState, ImportUserAction } from '../utils/codeMatching';
import {
  LineTableColumnSettings, loadColumnVisibility, saveColumnVisibility,
} from '../components/LineTableColumnSettings';
import { INVOICE_LINE_COLUMN_DEFS } from '../config/salesDocumentLineColumns';

const FI_ORANGE = '#F57C00';
const INVOICE_DOC_MAX_WIDTH = 1280;
const INVOICE_MASTHEAD_EDIT_INSET = 2;
const TEMPLATES = ['Original', 'Service Invoice', 'Project Invoice'];
const INVOICE_TABS = ['Jobs & inventory', 'History'] as const;

const INVOICE_LINE_COL_IDS = INVOICE_LINE_COLUMN_DEFS.map(c => c.id);

const FOOTER_BTN_INFO: Record<
  | 'preview' | 'close' | 'download' | 'email' | 'copy' | 'createJob' | 'addPayment'
  | 'submitApproval' | 'exportXero' | 'deleteInvoice',
  ButtonInfo
> = {
  preview: { title: 'Preview', description: 'Read-only customer-facing invoice view.', example: 'Preview INV-0105 before emailing.' },
  close: { title: 'Close', description: 'Return to Invoices list.', example: 'Close after checking totals.' },
  download: { title: 'Download', description: 'Save invoice PDF.', example: 'Download INV-0105 for records.' },
  email: { title: 'Email', description: 'Email invoice PDF to customer.', example: 'Email INV-0102 to accounts.' },
  copy: { title: 'Copy', description: 'Duplicate to Quote, PO, or Invoice.', example: 'Copy INV-0105 to a new draft.' },
  createJob: { title: 'Create job', description: 'Create field job from invoice lines.', example: 'Create job from INV-0105.' },
  addPayment: { title: 'Add payment', description: 'Record customer payment.', example: 'Add $400 EFT against INV-0095.' },
  submitApproval: { title: 'Submit for Approval', description: 'Send draft for internal approval.', example: 'Submit INV-0108 for manager sign-off.' },
  exportXero: { title: 'Export to Xero', description: 'Push to Xero as AR invoice.', example: 'Export INV-0102 to Xero.' },
  deleteInvoice: { title: 'Delete', description: 'Remove draft invoice.', example: 'Delete wrong-customer INV-0108.' },
};

function deriveSales(purchase: number, markup: number): number {
  return Math.round(purchase * (1 + markup / 100) * 100) / 100;
}
function lineAmount(l: InvoiceLineDetail): number {
  const base = l.salesPrice * l.qty;
  const disc = l.discount ? (base * l.discount) / 100 : 0;
  return base - disc;
}
function lineCost(l: InvoiceLineDetail): number {
  return l.purchasePrice * l.qty;
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return d; }
}

const DISCLOSURE_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 0.75,
  mx: -0.5,
  px: 0.75,
  py: 0.5,
  borderRadius: 1,
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#faf8ff' },
} as const;
const DISCLOSURE_ROW_MB = 0.35;
const DISCLOSURE_ACTION_GAP = 1.25;

export const InvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    invoices, invoiceLineDetails, invoicePayments, sites, projects,
    updateInvoice, setInvoiceStatus, updateInvoiceLineDetails, deleteInvoice, removeInvoicePayment,
  } = useData();

  const invoice = invoices.find(inv => inv.id === id);

  const [tab, setTab] = useState(0);
  const [colVisible, setColVisible] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility('invoice-line-columns-v1', INVOICE_LINE_COL_IDS));
  const toggleCol = (id: string) => {
    setColVisible(prev => {
      const next = { ...prev, [id]: prev[id] === false };
      saveColumnVisibility('invoice-line-columns-v1', next);
      return next;
    });
  };
  const showCol = (id: string) => colVisible[id] !== false;
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [createJobAnchor, setCreateJobAnchor] = useState<null | HTMLElement>(null);

  const [detailsExpanded, setDetailsExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('invoice-details-expanded-v1') === '1'; } catch { return false; }
  });
  const [linesExpanded, setLinesExpandedRaw] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('invoice-lines-expanded-v1');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  const [paymentsExpanded, setPaymentsExpandedRaw] = useState<boolean>(false);
  const paymentsExpandedKey = id ? `invoice-payments-expanded-v1-${id}` : '';
  const [invoiceDocUrl, setInvoiceDocUrl] = useState<string | null>(null);
  const [invoiceDocFile, setInvoiceDocFile] = useState<File | null>(null);

  // v1.37 — supplier-document import uses the shared SupplierImportPreviewDialog
  // (same modal as PO + Bill); the source doc is its collapsible left rail.
  // An invoice has no supplier price book, so deps are neutral.
  const [invoiceParsing, setInvoiceParsing] = useState(false);
  const [invoiceImportNotice, setInvoiceImportNotice] = useState<string | null>(null);
  const [pendingInvoiceImport, setPendingInvoiceImport] = useState<null | {
    lines: ParsedImportLine[]; documentType?: string; supplierInvoiceNo?: string;
    demo?: boolean; demoMessage?: string; fileName?: string;
  }>(null);
  const [invoicePreviewResolutions, setInvoicePreviewResolutions] = useState<Record<number, ImportLineState>>({});
  const [invoiceImportPreviewCollapsed, setInvoiceImportPreviewCollapsed] = useState(true);
  const invoiceImportDeps = useMemo((): ImportPreviewDeps => ({
    supplierId: '',
    supplierName: 'Imported document',
    products: [],
    bookForSupplier: [],
    lookupSupplierCode: () => undefined,
    addProduct: () => {},
  }), []);

  const toggleDetailsExpanded = () => {
    const next = !detailsExpanded;
    setDetailsExpandedRaw(next);
    try { localStorage.setItem('invoice-details-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const toggleLinesExpanded = () => {
    const next = !linesExpanded;
    setLinesExpandedRaw(next);
    try { localStorage.setItem('invoice-lines-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const togglePaymentsExpanded = () => {
    const next = !paymentsExpanded;
    setPaymentsExpandedRaw(next);
    if (paymentsExpandedKey) {
      try { localStorage.setItem(paymentsExpandedKey, next ? '1' : '0'); } catch {}
    }
  };
  const expandPayments = () => {
    setPaymentsExpandedRaw(true);
    if (paymentsExpandedKey) {
      try { localStorage.setItem(paymentsExpandedKey, '1'); } catch {}
    }
  };
  const disclosureKeyDown = (toggle: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  };

  const [lines, setLines] = useState<InvoiceLineDetail[]>([]);
  useEffect(() => {
    setLines(invoiceLineDetails.filter(l => l.invoiceId === id));
  }, [id, invoiceLineDetails]);

  const payments = useMemo(
    () => invoicePayments.filter(p => p.invoiceId === id),
    [invoicePayments, id],
  );

  useEffect(() => {
    if (!id || !invoice) return;
    try {
      const stored = localStorage.getItem(`invoice-payments-expanded-v1-${id}`);
      if (stored !== null) {
        setPaymentsExpandedRaw(stored === '1');
        return;
      }
    } catch { /* noop */ }
    if (payments.length > 0 || invoice.owingAmount > 0) {
      setPaymentsExpandedRaw(true);
    }
  }, [id, invoice, payments.length, invoice?.owingAmount]);

  const [purchasesTax, setPurchasesTax] = useState<TaxMode>('Exclusive');
  const [salesTax, setSalesTax] = useState<TaxMode>('Exclusive');
  useEffect(() => {
    if (invoice) {
      setPurchasesTax(invoice.purchasesTax ?? 'Exclusive');
      setSalesTax(invoice.salesTax ?? 'Exclusive');
    }
  }, [invoice]);

  const totals = useMemo(() => {
    const servicesEx = lines.filter(l => l.lineType === 'service').reduce((s, l) => s + lineAmount(l), 0);
    const productsEx  = lines.filter(l => l.lineType === 'product').reduce((s, l) => s + lineAmount(l), 0);
    const subtotal    = servicesEx + productsEx;
    const tax         = lines.filter(l => l.taxable).reduce((s, l) => s + lineAmount(l) * 0.1, 0);
    const cost        = lines.reduce((s, l) => s + lineCost(l), 0);
    const margin      = subtotal > 0 ? ((subtotal - cost) / subtotal) * 100 : 0;
    const markupPct   = cost > 0 ? ((subtotal - cost) / cost) * 100 : 0;
    return { servicesEx, productsEx, subtotal, tax, total: subtotal + tax, cost, margin, markupPct };
  }, [lines]);

  if (!invoice) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: '#888', mb: 1 }}>Invoice not found</Typography>
        <Button onClick={() => navigate('/invoices-list')} sx={{ textTransform: 'none', color: BRAND }}>
          ← Back to Invoices
        </Button>
      </Box>
    );
  }

  const customer = sites.find(s => s.id === invoice.customerId);
  const project  = projects.find(p => p.id === invoice.projectId);
  const customerName = customer?.businessName ?? invoice.customerName ?? '—';

  const commitLines = (next: InvoiceLineDetail[]) => {
    setLines(next);
    updateInvoiceLineDetails(invoice.id, next);
  };

  const updateLine = (idx: number, patch: Partial<InvoiceLineDetail>) => {
    commitLines(lines.map((l, i) => {
      if (i !== idx) return l;
      const merged = { ...l, ...patch };
      if ('markup' in patch && merged.purchasePrice > 0) merged.salesPrice = deriveSales(merged.purchasePrice, merged.markup);
      if ('purchasePrice' in patch && !('salesPrice' in patch)) merged.salesPrice = deriveSales(merged.purchasePrice, merged.markup);
      if ('salesPrice' in patch && merged.purchasePrice > 0) merged.markup = Math.round(((merged.salesPrice / merged.purchasePrice) - 1) * 10000) / 100;
      return merged;
    }));
  };

  const addLine = (lineType: POLineType) => {
    commitLines([...lines, {
      invoiceId: invoice.id, lineType, inventoryCode: '', description: '',
      qty: 1, purchasePrice: 0, markup: lineType === 'service' ? 100 : 45,
      salesPrice: 0, discount: 0, taxable: true,
    }]);
  };
  const removeLine = (idx: number) => commitLines(lines.filter((_, i) => i !== idx));

  const handleAttachFile = (file: File) => {
    updateInvoice(invoice!.id, { attachments: [...(invoice!.attachments ?? []), file.name] });
  };

  const showInvoiceImportPreview = (
    parsed: ParsedImportLine[],
    file: File | null,
    meta: { documentType?: string; supplierInvoiceNo?: string; demo?: boolean; demoMessage?: string },
  ) => {
    if (parsed.length === 0) { setInvoiceImportNotice('No line items found in that document.'); return; }
    setInvoicePreviewResolutions(buildInitialImportResolutions(parsed, invoiceImportDeps));
    setPendingInvoiceImport({ lines: parsed, fileName: file?.name, ...meta });
    setInvoiceImportPreviewCollapsed(true);
    setInvoiceImportNotice(null);
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setInvoiceDocFile(file);
      setInvoiceDocUrl(URL.createObjectURL(file));
    }
  };

  const setInvoicePreviewAction = (idx: number, action: ImportUserAction) => {
    if (!pendingInvoiceImport) return;
    setInvoicePreviewResolutions(prev =>
      applyImportPreviewAction(pendingInvoiceImport.lines, prev, idx, action, invoiceImportDeps));
  };
  const batchInvoicePreviewAction = (action: 'autoCreate' | 'service') => {
    if (!pendingInvoiceImport) return;
    setInvoicePreviewResolutions(prev =>
      batchImportPreviewAction(pendingInvoiceImport.lines, prev, action, invoiceImportDeps));
  };
  const invoicePreviewNewCount = pendingInvoiceImport
    ? countPreviewNewLines(pendingInvoiceImport.lines, invoicePreviewResolutions, invoiceImportDeps)
    : 0;

  const applyInvoiceImport = (
    resolutionsOverride?: Record<number, ImportLineState>,
    codeMode: 'useSupplierCodes' | 'autoCreate' = 'autoCreate',
  ) => {
    if (!pendingInvoiceImport) return;
    const res = resolutionsOverride ?? invoicePreviewResolutions;
    const mintedInBatch: string[] = [];
    const newLines: InvoiceLineDetail[] = pendingInvoiceImport.lines.map((l, i) => {
      let r = res[i] ?? buildImportLineResolution(l, invoiceImportDeps);
      const sc = (l.supplierCode || '').trim();
      let internalCode = '';
      let lineType: 'product' | 'service' = 'product';
      if (codeMode === 'useSupplierCodes') {
        lineType = r.status === 'service' || r.userAction === 'service' ? 'service' : 'product';
        internalCode = lineType === 'service' ? '' : sc;
      } else {
        if (r.status === 'new' && r.userAction === 'pending') r = { ...r, userAction: 'autoCreate' };
        ({ internalCode, lineType } = resolveImportPreviewInternal(l, r, invoiceImportDeps, mintedInBatch));
      }
      const purchasePrice = l.unitPrice ?? 0;
      const markup = lineType === 'service' ? 100 : 45;
      return {
        invoiceId: invoice!.id,
        lineType,
        inventoryCode: internalCode,
        description: l.description || '',
        qty: l.qty ?? 1,
        purchasePrice,
        markup,
        salesPrice: deriveSales(purchasePrice, markup),
        discount: 0,
        taxable: true,
      };
    }).filter(l => l.description.trim());
    commitLines([...lines, ...newLines]);
    setInvoiceImportNotice(`${newLines.length} line${newLines.length !== 1 ? 's' : ''} imported.`);
    setPendingInvoiceImport(null);
    setInvoicePreviewResolutions({});
    setInvoiceDocFile(null);
    setInvoiceDocUrl(null);
  };
  const useSupplierCodesAndApplyInvoice = () => applyInvoiceImport(undefined, 'useSupplierCodes');
  const autoCreateInternalCodesAndApplyInvoice = () => applyInvoiceImport(undefined, 'autoCreate');
  const discardInvoiceImport = () => {
    setPendingInvoiceImport(null);
    setInvoicePreviewResolutions({});
    setInvoiceDocFile(null);
    setInvoiceDocUrl(null);
  };

  const parseInvoiceFile = (file: File) => {
    setInvoiceParsing(true);
    setInvoiceImportNotice(null);
    const fd = new FormData();
    fd.append('file', file);
    fetch('/api/parse-invoice', { method: 'POST', body: fd })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data.error) {
          setInvoiceImportNotice(data.friendly ?? data.error ?? 'AI parsing unavailable — add lines manually.');
          return;
        }
        showInvoiceImportPreview(data.lines ?? [], file, {
          documentType: 'invoice',
          supplierInvoiceNo: data.invoiceNo,
        });
      })
      .catch(() => setInvoiceImportNotice('Could not reach the parse service — add lines manually.'))
      .finally(() => setInvoiceParsing(false));
  };

  const handleDeleteInvoice = () => {
    const msg = invoice!.status === 'Draft'
      ? `Delete ${invoice!.id}? This cannot be undone.`
      : `Delete ${invoice!.id}? This invoice is ${invoice!.status}. Deleting is permanent.`;
    if (window.confirm(msg)) {
      deleteInvoice(invoice!.id);
      navigate('/invoices-list');
    }
  };

  const createdLine = invoice.createdBy
    ? `Created by ${invoice.createdBy}${invoice.createdAt ? `, ${new Date(invoice.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}`
    : '';

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Page header (identity only — verbs live in footer) ─────────── */}
      <Box className="no-print" sx={{ maxWidth: INVOICE_DOC_MAX_WIDTH, mx: 'auto', width: '100%', flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices-list')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666',
                '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
              Back
            </Button>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: FI_ORANGE }}>
              {invoice.id}
            </Typography>
            <StatusChip
              status={invoice.status}
              onClick={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
              deleteIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
              onDelete={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
              sx={{ height: 26, cursor: 'pointer' }}
            />
            <Tooltip title="Template" arrow>
              <Select
                size="small"
                value={invoice.template ?? 'Default Template'}
                onChange={e => updateInvoice(invoice.id, { template: e.target.value })}
                sx={{ fontSize: '0.72rem', height: 28, minWidth: 160, '& .MuiSelect-select': { py: 0.5 } }}
              >
                {TEMPLATES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.72rem' }}>{t}</MenuItem>)}
              </Select>
            </Tooltip>
          </Stack>
          {createdLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              {createdLine}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ borderBottom: '1px solid transparent', mb: 1.5 }}>
          {INVOICE_TABS.map((label, i) => (
            <Typography key={label}
              onClick={() => setTab(i)}
              sx={{
                fontSize: '0.8rem', fontWeight: i === tab ? 700 : 600,
                color: i === tab ? FI_ORANGE : '#888', pb: 0.6, cursor: 'pointer',
                borderBottom: i === tab ? `2px solid ${FI_ORANGE}` : '2px solid transparent',
                '&:hover': { color: i === tab ? FI_ORANGE : BRAND },
              }}>
              {label}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Menu
        anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Set status
          </Typography>
        </Box>
        {INVOICE_STATUSES.map(s => {
          const tone = STATUS_TONE[getStatusTone(s)];
          const isCurrent = s === invoice.status;
          return (
            <MenuItem key={s} selected={isCurrent}
              onClick={() => { setInvoiceStatus(invoice.id, s); setStatusAnchor(null); }}
              sx={{ py: 0.75, px: 1.5, ...(isCurrent && { backgroundColor: tone.bg }) }}>
              <StatusChip status={s} dense />
            </MenuItem>
          );
        })}
      </Menu>

      <Paper sx={{ maxWidth: INVOICE_DOC_MAX_WIDTH, mx: 'auto', width: '100%', p: 4,
        border: '1px solid #e0e0e0', backgroundColor: '#fff' }} id="invoice-print-area">

        {/* ── Masthead card — purple cascade shell ─────────────────────── */}
        <Box className="no-print po-masthead-card" sx={{
          backgroundColor: '#faf8ff',
          border: '1px solid #e0d5f5',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{ p: 2 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={toggleDetailsExpanded}
              onKeyDown={disclosureKeyDown(toggleDetailsExpanded)}
              sx={{ cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, py: 0.25,
                '&:hover': { backgroundColor: '#f3edfc' } }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND,
                  textTransform: 'uppercase', letterSpacing: 1 }}>
                  Invoice
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>
                    Edit details
                  </Typography>
                  <ExpandMoreIcon sx={{
                    fontSize: 18, color: BRAND,
                    transform: detailsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }} />
                </Stack>
              </Stack>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2a2a2a', mt: 0.3 }}>
                {customerName}
              </Typography>
              <Stack direction="row" spacing={3.5} mt={1} flexWrap="wrap" useFlexGap>
                {[
                  ['Invoice Date', fmtDate(invoice.invoiceDate)],
                  ['Due', fmtDate(invoice.dueDate)],
                  ['Project', project?.name ?? '—'],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888',
                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 0.1 }}>{value}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
          {detailsExpanded && (
            <>
              <Divider sx={{ borderColor: '#e0d5f5' }} />
              <Box sx={{ px: INVOICE_MASTHEAD_EDIT_INSET, pb: INVOICE_MASTHEAD_EDIT_INSET, pt: 1.25 }}>
                <InvoiceEditForm invoice={invoice} />
              </Box>
            </>
          )}
        </Box>

        <Divider className="no-print" sx={{ my: 1.25 }} />

        {/* Import + attach — consistent with PO / Quote / Bill */}
        <Box className="no-print" sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {invoiceImportNotice && (
            <Alert severity="info" onClose={() => setInvoiceImportNotice(null)} sx={{ fontSize: '0.78rem', py: 0.3 }}>
              {invoiceImportNotice}
            </Alert>
          )}
          <AttachFileZone
            onFile={handleAttachFile}
            subtitle="Drag files here · attached for reference, not imported into line items"
          />
          <AiImportBanner
            onFile={parseInvoiceFile}
            parsing={invoiceParsing}
            title="Import invoice lines"
            subtitle="Drag a supplier document PDF/image · AI reads the lines, you review them before they’re added"
            parsingLabel="Reading the document…"
            accept="application/pdf,image/*"
            buttonLabel="Choose file"
          />
          {(invoice.attachments ?? []).length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {invoice.attachments!.map((name, i) => (
                <Chip key={i} label={name} size="small"
                  onDelete={() => updateInvoice(invoice.id, {
                    attachments: (invoice.attachments ?? []).filter((_, j) => j !== i),
                  })}
                  sx={{ fontSize: '0.65rem', height: 22 }} />
              ))}
            </Stack>
          )}
        </Box>

        {/* ── Tab body ─────────────────────────────────────────────────── */}
        {tab === 0 ? (
          <>
            {/* Line Items — cascade disclosure */}
            <Box
              className="no-print"
              role="button"
              tabIndex={0}
              onClick={toggleLinesExpanded}
              onKeyDown={disclosureKeyDown(toggleLinesExpanded)}
              sx={{ ...DISCLOSURE_HEADER_SX, mb: linesExpanded ? 1.5 : DISCLOSURE_ROW_MB }}
            >
              <ExpandMoreIcon sx={{
                fontSize: 20, color: BRAND,
                transform: linesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
              }} />
              <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>
                Line Items
              </Typography>
              <Chip
                label={lines.length === 0 ? 'None' : `${lines.length} line${lines.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700,
                  backgroundColor: lines.length > 0 ? '#e8f5e9' : '#f5f5f5',
                  color: lines.length > 0 ? '#2e7d32' : '#888' }}
              />
              {!linesExpanded && lines.length > 0 && (
                <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem' }}>
                  · <strong style={{ color: BRAND }}>${totals.total.toFixed(2)}</strong> inc GST
                  · <span style={{ color: BRAND, fontWeight: 700 }}>{totals.margin.toFixed(1)}% margin</span>
                </Typography>
              )}
            </Box>

            {linesExpanded && (
              <Box>
                <Box sx={{ px: 0, py: 1.2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
                  backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #f0f0f0', mb: 1.5 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>Markup (%)</Typography>
                    <Chip label={totals.markupPct.toFixed(2)} size="small"
                      sx={{ backgroundColor: '#f3edfc', color: BRAND, fontWeight: 700, fontSize: '0.7rem' }} />
                  </Stack>
                  <Box flex={1} />
                  <TaxSelect label="Purchases are tax" value={purchasesTax}
                    onChange={v => { setPurchasesTax(v); updateInvoice(invoice.id, { purchasesTax: v }); }} />
                  <TaxSelect label="Sales are tax" value={salesTax}
                    onChange={v => { setSalesTax(v); updateInvoice(invoice.id, { salesTax: v }); }} />
                </Box>

                {lines.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <InboxIcon sx={{ fontSize: 44, color: '#e0e0e0', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#aaa', mb: 0.5 }}>No line items yet</Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: '#bbb', mb: 2 }}>Add a product or service line to build the invoice.</Typography>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addLine('product')} sx={BTN_STANDARD}>Add product</Button>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addLine('service')} sx={BTN_STANDARD}>Add service</Button>
                    </Stack>
                  </Box>
                ) : (
                  <Table size="small" sx={{ mb: 1.5 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#fafafa' }}>
                        {showCol('type') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Type</TableCell>}
                        {showCol('inventoryCode') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Inventory Code</TableCell>}
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Qty</TableCell>
                        {showCol('purchase') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Purchase</TableCell>}
                        {showCol('markup') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Markup %</TableCell>}
                        {showCol('sales') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Sales</TableCell>}
                        {showCol('discount') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Disc %</TableCell>}
                        {showCol('tax') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Tax</TableCell>}
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Amount</TableCell>
                        <TableCell sx={{ width: 36 }} />
                        <LineTableColumnSettings
                          columns={INVOICE_LINE_COLUMN_DEFS}
                          visible={colVisible}
                          onToggle={toggleCol}
                          documentKind="invoice"
                          templateName={invoice?.template}
                          returnTo={invoice ? `/invoices/${invoice.id}` : undefined}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map((l, idx) => (
                        <TableRow key={idx} hover>
                          {showCol('type') && (
                          <TableCell sx={{ minWidth: 96 }}>
                            <Select value={l.lineType} variant="standard" disableUnderline
                              onChange={e => updateLine(idx, { lineType: e.target.value as POLineType })}
                              sx={{ fontSize: '0.76rem', fontWeight: 600 }}>
                              <MenuItem value="product">Product</MenuItem>
                              <MenuItem value="service">Service</MenuItem>
                            </Select>
                          </TableCell>
                          )}
                          {showCol('inventoryCode') && (
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField variant="standard" value={l.inventoryCode}
                              onChange={e => updateLine(idx, { inventoryCode: e.target.value })}
                              InputProps={{ disableUnderline: true, sx: { fontSize: '0.76rem' } }} placeholder="—" />
                          </TableCell>
                          )}
                          <TableCell sx={{ minWidth: 220 }}>
                            <TextField variant="standard" fullWidth value={l.description}
                              onChange={e => updateLine(idx, { description: e.target.value })}
                              InputProps={{ disableUnderline: true, sx: { fontSize: '0.78rem' } }} placeholder="Description" />
                          </TableCell>
                          <TableCell sx={{ width: 64 }}><NumCell value={l.qty} onChange={v => updateLine(idx, { qty: v })} /></TableCell>
                          {showCol('purchase') && <TableCell sx={{ width: 80 }}><NumCell value={l.purchasePrice} onChange={v => updateLine(idx, { purchasePrice: v })} decimals /></TableCell>}
                          {showCol('markup') && <TableCell sx={{ width: 72 }}><NumCell value={l.markup} onChange={v => updateLine(idx, { markup: v })} decimals /></TableCell>}
                          {showCol('sales') && <TableCell sx={{ width: 80 }}><NumCell value={l.salesPrice} onChange={v => updateLine(idx, { salesPrice: v })} decimals /></TableCell>}
                          {showCol('discount') && <TableCell sx={{ width: 64 }}><NumCell value={l.discount} onChange={v => updateLine(idx, { discount: v })} decimals /></TableCell>}
                          {showCol('tax') && (
                          <TableCell sx={{ width: 64 }}>
                            <Select value={l.taxable ? 'YES' : 'NO'} variant="standard" disableUnderline
                              onChange={e => updateLine(idx, { taxable: e.target.value === 'YES' })}
                              sx={{ fontSize: '0.72rem', fontWeight: 700, color: l.taxable ? '#2e7d32' : '#999' }}>
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                            </Select>
                          </TableCell>
                          )}
                          <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right' }}>
                            {lineAmount(l).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeLine(idx)}>
                              <DeleteIcon sx={{ fontSize: 14, color: '#ccc' }} />
                            </IconButton>
                          </TableCell>
                          <TableCell className="no-print" sx={{ width: 40 }} />
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <Box sx={{ p: 1.5, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addLine('product')} sx={LINK_BLUE}>Add product</Button>
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addLine('service')} sx={LINK_BLUE}>Add service</Button>
                  <Box flex={1} />
                  <Button size="small" sx={BTN_STANDARD}>+ Section</Button>
                  <Button size="small" sx={BTN_STANDARD}>Import CSV</Button>
                  <Button size="small" sx={BTN_STANDARD}>Export CSV</Button>
                  <Button size="small" sx={BTN_STANDARD}>Create Project Budget</Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
                  <Paper sx={{ p: 2, minWidth: 280, border: '1px solid #e0e0e0' }}>
                    {[
                      ['Total Services (Ex)', totals.servicesEx],
                      ['Total Products (Ex)', totals.productsEx],
                      ['Subtotal', totals.subtotal],
                      ['Tax amount', totals.tax],
                    ].map(([l, v]) => (
                      <Stack key={l as string} direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption">{(v as number).toFixed(2)}</Typography>
                      </Stack>
                    ))}
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">Paid amount</Typography>
                      <Typography variant="caption">{invoice.paidAmount.toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" sx={{ color: invoice.owingAmount > 0 ? '#b71c1c' : '#2e7d32', fontWeight: 700 }}>Owing amount</Typography>
                      <Typography variant="caption" sx={{ color: invoice.owingAmount > 0 ? '#b71c1c' : '#2e7d32', fontWeight: 700 }}>{invoice.owingAmount.toFixed(2)}</Typography>
                    </Stack>
                    <Divider sx={{ my: 0.5 }} />
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={800}>TOTAL</Typography>
                      <Typography variant="body2" fontWeight={800}>{totals.total.toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700 }}>Margin</Typography>
                      <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700 }}>{totals.margin.toFixed(2)}%</Typography>
                    </Stack>
                  </Paper>
                </Box>
              </Box>
            )}

            {/* Payments — inline disclosure (mirrors PO Credit Notes) */}
            <Divider className="no-print" sx={{ my: 1.25 }} />
            <Box className="no-print">
              <Box
                role="button"
                tabIndex={0}
                onClick={togglePaymentsExpanded}
                onKeyDown={disclosureKeyDown(togglePaymentsExpanded)}
                sx={{ ...DISCLOSURE_HEADER_SX, mb: paymentsExpanded ? DISCLOSURE_ACTION_GAP : DISCLOSURE_ROW_MB }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: 20, color: BRAND,
                  transform: paymentsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.15s ease',
                }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>
                  Payments
                </Typography>
                <Chip
                  label={
                    payments.length === 0
                      ? 'None'
                      : `${payments.length} payment${payments.length !== 1 ? 's' : ''}`
                  }
                  size="small"
                  sx={{
                    fontSize: '0.65rem', height: 18, fontWeight: 700,
                    backgroundColor: payments.length === 0
                      ? '#f5f5f5'
                      : invoice.owingAmount > 0
                        ? '#ffebee'
                        : '#e8f5e9',
                    color: payments.length === 0
                      ? '#888'
                      : invoice.owingAmount > 0
                        ? '#b71c1c'
                        : '#2e7d32',
                  }}
                />
                {!paymentsExpanded && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#888', fontSize: '0.68rem',
                      fontStyle: payments.length === 0 ? 'italic' : 'normal',
                    }}
                  >
                    {payments.length === 0
                      ? '· No payments recorded'
                      : (
                        <>
                          · Paid <strong style={{ color: '#555' }}>${invoice.paidAmount.toFixed(2)}</strong>
                          {' · '}
                          Owing{' '}
                          <strong style={{ color: invoice.owingAmount > 0 ? '#b71c1c' : '#2e7d32' }}>
                            ${invoice.owingAmount.toFixed(2)}
                          </strong>
                        </>
                      )}
                  </Typography>
                )}
              </Box>

              {paymentsExpanded && (
                <InfoableButton info={FOOTER_BTN_INFO.addPayment}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 14 }} />}
                    onClick={() => alert('Add payment — backlog (FieldInsight parity stub).')}
                    sx={{ ...BTN_PURPLE, mb: 1.2 }}
                  >
                    Add payment
                  </Button>
                </InfoableButton>
              )}

              {paymentsExpanded && (payments.length === 0 ? (
                <Box sx={{
                  py: 1.5, textAlign: 'center',
                  border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff',
                }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#b0a0d8', fontWeight: 600 }}>
                    No payments recorded against this invoice
                  </Typography>
                </Box>
              ) : (
                <>
                  <Table size="small" sx={{ mb: 1, '& td, & th': { fontSize: '0.72rem' } }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                        {['#', 'Date created', 'Amount paid', 'Paid date', 'Payment method', 'Added by', 'Reference', ''].map(h => (
                          <TableCell key={h || 'actions'} sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((p, idx) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ color: '#888', width: 28 }}>{idx + 1}</TableCell>
                          <TableCell>{fmtDate(p.dateCreated)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{p.amountPaid.toFixed(2)}</TableCell>
                          <TableCell>{fmtDate(p.paidDate)}</TableCell>
                          <TableCell>{p.paymentMethod}</TableCell>
                          <TableCell>{p.addedBy}</TableCell>
                          <TableCell sx={{ color: '#555' }}>{p.reference || '—'}</TableCell>
                          <TableCell sx={{ width: 36 }}>
                            <IconButton
                              size="small"
                              aria-label="Remove payment"
                              onClick={() => {
                                if (window.confirm('Remove this payment?')) removeInvoicePayment(p.id);
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 14, color: '#e57373' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Stack direction="row" justifyContent="flex-end" mt={0.5}>
                    <Box sx={{ minWidth: 220 }}>
                      <Stack direction="row" justifyContent="space-between" mb={0.4}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>Paid amount</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{invoice.paidAmount.toFixed(2)}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{
                          fontSize: '0.72rem', fontWeight: 700,
                          color: invoice.owingAmount > 0 ? '#b71c1c' : '#2e7d32',
                        }}>
                          Owing amount
                        </Typography>
                        <Typography sx={{
                          fontSize: '0.72rem', fontWeight: 700,
                          color: invoice.owingAmount > 0 ? '#b71c1c' : '#2e7d32',
                        }}>
                          {invoice.owingAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </>
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#aaa' }}>
              No history entries yet.
            </Typography>
          </Box>
        )}

        {/* Version lives in app Layout footer only — no mid-page prototype stamp. */}

        {/* ── Bottom action bar — doc tools scroll; lifecycle sticky below ─ */}
        <Box className="no-print" sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
          <Stack direction="row" spacing={0.8} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.preview}>
                <Button size="small" disableElevation startIcon={<VisibilityIcon />}
                  onClick={() => alert('Preview invoice — backlog')}
                  sx={BTN_GHOST_DOC}>Preview</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.download}>
                <Button size="small" disableElevation startIcon={<DownloadIcon />}
                  onClick={() => window.print()} sx={BTN_GHOST_DOC}>Download</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.email}>
                <Button size="small" disableElevation startIcon={<EmailIcon />}
                  onClick={() => alert('Email invoice to customer — backlog')}
                  sx={BTN_GHOST_DOC}>Email</Button>
              </InfoableButton>
            </Stack>
            <Box sx={{ width: 24, flexShrink: 0 }} />
            <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.copy}>
                <Button size="small" disableElevation startIcon={<ContentCopyIcon />}
                  onClick={() => alert('Copy invoice — backlog')} sx={BTN_GHOST_DOC}>Copy</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.createJob}>
                <Button size="small" disableElevation startIcon={<WorkIcon />}
                  endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  onClick={e => setCreateJobAnchor(e.currentTarget)}
                  sx={BTN_GHOST_DOC}>Create job</Button>
              </InfoableButton>
              <Menu anchorEl={createJobAnchor} open={Boolean(createJobAnchor)} onClose={() => setCreateJobAnchor(null)}>
                <MenuItem onClick={() => { setCreateJobAnchor(null); alert('Create job — backlog.'); }}>Create job</MenuItem>
                <MenuItem onClick={() => { setCreateJobAnchor(null); alert('Create project jobs — backlog.'); }}>Create project jobs</MenuItem>
              </Menu>
              <InfoableButton info={FOOTER_BTN_INFO.addPayment}>
                <Button size="small" disableElevation startIcon={<AccountBalanceWalletOutlinedIcon />}
                  onClick={() => { expandPayments(); alert('Add payment — backlog'); }}
                  sx={BTN_GHOST_DOC}>Add payment</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.exportXero}>
                <Button size="small" disableElevation startIcon={<OpenInNewIcon />}
                  onClick={() => alert('Export to Xero — backlog')} sx={BTN_GHOST_DOC}>Export to Xero</Button>
              </InfoableButton>
            </Stack>
          </Stack>
        </Box>

        {/* Row 2 — Lifecycle (sticky: Close · primary · Delete) */}
        <Box className="no-print po-sticky-nav" sx={PO_STICKY_LIFECYCLE_SX}>
            <InfoableButton info={FOOTER_BTN_INFO.close}>
              <Button size="small" disableElevation
                onClick={() => navigate('/invoices-list')}
                sx={BTN_STANDARD}>Close</Button>
            </InfoableButton>

            <Box sx={{ width: 24, flexShrink: 0 }} />

            {invoice.status === 'Draft' && (
              <InfoableButton info={FOOTER_BTN_INFO.submitApproval}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => setInvoiceStatus(invoice.id, 'Awaiting Approval')}
                  sx={BTN_STATUS_GREEN}>Submit for Approval</Button>
              </InfoableButton>
            )}
            {invoice.status === 'Awaiting Approval' && (
              <InfoableButton info={FOOTER_BTN_INFO.submitApproval}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => { setInvoiceStatus(invoice.id, 'Approved'); alert('Invoice approved (mock).'); }}
                  sx={BTN_STATUS_GREEN}>Approve</Button>
              </InfoableButton>
            )}
            {(invoice.status === 'Approved' || invoice.status === 'Sent') && (
              <InfoableButton info={FOOTER_BTN_INFO.email}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => { setInvoiceStatus(invoice.id, 'Sent'); updateInvoice(invoice.id, { sent: true }); alert('Invoice emailed (mock).'); }}
                  sx={BTN_STATUS_GREEN}>Email to customer</Button>
              </InfoableButton>
            )}
            <Box sx={PO_STICKY_LIFECYCLE_PUSH} />

            <Box sx={PO_STICKY_LIFECYCLE_TRAIL}>
              <InfoableButton info={FOOTER_BTN_INFO.deleteInvoice}>
                <Button size="small" disableElevation startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
                  onClick={handleDeleteInvoice}
                  sx={BTN_GHOST_DANGER}>Delete</Button>
              </InfoableButton>
            </Box>
        </Box>
      </Paper>

      {/* v1.37 — shared import-preview modal (same control as PO + Bill). The
          source document is the modal's collapsible left rail; supplier-code
          tracking is off because an invoice has no supplier price book. */}
      <SupplierImportPreviewDialog
        open={!!pendingInvoiceImport}
        lines={pendingInvoiceImport?.lines ?? []}
        resolutions={invoicePreviewResolutions}
        targetLabel={invoice.id}
        targetKind="invoice"
        existingLineCount={lines.length}
        trackSupplierCodes={false}
        previewNewCount={invoicePreviewNewCount}
        demo={pendingInvoiceImport?.demo}
        demoMessage={pendingInvoiceImport?.demoMessage}
        documentType={pendingInvoiceImport?.documentType}
        supplierInvoiceNo={pendingInvoiceImport?.supplierInvoiceNo}
        fileName={invoiceDocFile?.name ?? pendingInvoiceImport?.fileName}
        fileUrl={invoiceDocUrl}
        fileType={invoiceDocFile?.type}
        previewCollapsed={invoiceImportPreviewCollapsed}
        onTogglePreviewCollapsed={() => setInvoiceImportPreviewCollapsed(c => !c)}
        onSetPreviewAction={setInvoicePreviewAction}
        onBatchAction={batchInvoicePreviewAction}
        onUseSupplierCodes={useSupplierCodesAndApplyInvoice}
        onAutoCreateInternalCodes={autoCreateInternalCodesAndApplyInvoice}
        onDiscard={discardInvoiceImport}
      />
    </Box>
  );
};

const TaxSelect: React.FC<{ label: string; value: TaxMode; onChange: (v: TaxMode) => void }> = ({ label, value, onChange }) => (
  <Stack direction="row" spacing={0.8} alignItems="center">
    <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>{label}</Typography>
    <FormControl size="small">
      <Select value={value} onChange={e => onChange(e.target.value as TaxMode)}
        sx={{ fontSize: '0.76rem', height: 28, minWidth: 110, '& .MuiSelect-select': { py: 0.4, px: 1 } }}>
        <MenuItem value="Exclusive">Exclusive</MenuItem>
        <MenuItem value="Inclusive">Inclusive</MenuItem>
      </Select>
    </FormControl>
  </Stack>
);

const NumCell: React.FC<{ value: number; onChange: (v: number) => void; decimals?: boolean }> = ({ value, onChange, decimals }) => (
  <TextField variant="standard" type="number"
    value={Number.isFinite(value) ? value : 0}
    onChange={e => onChange(parseFloat(e.target.value) || 0)}
    InputProps={{ disableUnderline: true, sx: { fontSize: '0.76rem' } }}
    inputProps={{ step: decimals ? '0.01' : '1', style: { textAlign: 'right' } }}
  />
);

export default InvoicePage;
